import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { lookupInvite, redeemInvite } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { AlertTriangle, CheckCircle2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

const LOGO = (
  // Same Wix-hosted logo we use in the email; mono filter darkens it on the
  // light card background so it reads cleanly on both light and dark themes.
  <img
    src="https://static.wixstatic.com/media/47e68b_9ba20c13e75f4752b5585a413624d1cc~mv2.png/v1/fill/w_342,h_72,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/Icon%20%2B%20Lutrom%20%2B%20Engineering.png"
    alt="Lutrom Engineering"
    className="h-9 w-auto"
  />
);

/**
 * /invite/:token — public, no-auth page where an invitee sets their password.
 *
 * 1. Pre-fetch the token so we can show "Hi {name}, finish setting up your
 *    Lutrom account" and reject expired / consumed tokens with a friendly
 *    "ask your admin to re-send" message.
 * 2. On submit POST the new password; if the redeem succeeds the backend
 *    returns a JWT — we drop it into the existing AuthContext via the same
 *    `setSession` exposed by `login()`, then navigate to the role-based home.
 */
const Invite = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { setSession } = useAuth();

  const [state, setState] = useState({ status: 'loading' });
  const [pwd, setPwd] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let alive = true;
    lookupInvite(token)
      .then(d => alive && setState({ status: 'ready', invite: d }))
      .catch(e => {
        if (!alive) return;
        const code = e?.response?.status;
        const detail = e?.response?.data?.detail;
        if (code === 410) setState({ status: 'expired', detail });
        else if (code === 404) setState({ status: 'notfound', detail });
        else setState({ status: 'error', detail: detail || 'Could not load invite' });
      });
    return () => { alive = false; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pwd.length < 8) return toast.error('Password must be at least 8 characters');
    if (pwd !== confirm) return toast.error("Passwords don't match");
    setSubmitting(true);
    try {
      const { token: jwt, user } = await redeemInvite(token, pwd);
      setSession(jwt, user);
      toast.success(`Welcome to Lutrom, ${user.name.split(' ')[0]}!`);
      // Land on the role's home — same logic as the login page.
      const home = user.role === 'admin' ? '/admin'
                 : user.role === 'employee' ? '/employee'
                 : user.role === 'subcontractor' ? '/subcontractor'
                 : '/customer';
      navigate(home, { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not set password');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4" data-testid="invite-page">
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden">
        <div className="bg-slate-900 px-6 py-5 text-center">{LOGO}</div>
        <div className="p-6">
          {state.status === 'loading' && (
            <div className="text-sm text-slate-500 text-center py-8">Checking your invite…</div>
          )}
          {state.status === 'expired' && (
            <Notice
              tone="amber"
              icon={AlertTriangle}
              title="This invite has expired"
              body={state.detail || 'Your invite link is no longer valid. Ask the admin who invited you to send a new one.'}
            />
          )}
          {state.status === 'notfound' && (
            <Notice
              tone="rose"
              icon={AlertTriangle}
              title="Invite not found"
              body="This link is invalid. Check that you copied it in full from the email — or ask your admin for a fresh invite."
            />
          )}
          {state.status === 'error' && (
            <Notice tone="rose" icon={AlertTriangle} title="Something went wrong" body={state.detail} />
          )}
          {state.status === 'ready' && (
            <form onSubmit={submit} className="space-y-4" data-testid="invite-form">
              <div>
                <div className="text-[11px] uppercase tracking-widest text-slate-500">Welcome</div>
                <h1 className="text-2xl font-display tracking-tight text-slate-900 mt-1">
                  Hi {state.invite.name?.split(' ')[0] || 'there'} 👋
                </h1>
                <p className="text-sm text-slate-600 mt-2">
                  You&rsquo;ve been invited as
                  {' '}<span className="font-medium text-slate-900 capitalize">{state.invite.role}</span>.
                  Set a password to finish setting up your account.
                </p>
                <div className="mt-3 px-3 py-2 bg-slate-50 border border-slate-200 rounded text-xs text-slate-700 font-mono break-all">
                  {state.invite.email}
                </div>
              </div>
              <div>
                <Label htmlFor="invite-pwd">Choose a password</Label>
                <Input
                  id="invite-pwd"
                  type="password"
                  className="mt-1.5"
                  value={pwd}
                  onChange={e => setPwd(e.target.value)}
                  autoFocus
                  data-testid="invite-pwd-input"
                  minLength={8}
                  required
                />
              </div>
              <div>
                <Label htmlFor="invite-confirm">Confirm password</Label>
                <Input
                  id="invite-confirm"
                  type="password"
                  className="mt-1.5"
                  value={confirm}
                  onChange={e => setConfirm(e.target.value)}
                  data-testid="invite-confirm-input"
                  minLength={8}
                  required
                />
              </div>
              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-slate-900 hover:bg-slate-800 h-11"
                data-testid="invite-submit-btn"
              >
                <ShieldCheck className="h-4 w-4 mr-2" />
                {submitting ? 'Setting password…' : 'Accept invite'}
              </Button>
              <p className="text-[11px] text-slate-500 text-center">
                Use at least 8 characters. We&rsquo;ll log you straight in once you submit.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

const Notice = ({ tone, icon: Icon, title, body }) => {
  const map = {
    amber: 'bg-amber-50 border-amber-200 text-amber-900',
    rose: 'bg-rose-50 border-rose-200 text-rose-900',
  };
  return (
    <div className={`border rounded-md p-4 flex items-start gap-3 ${map[tone] || ''}`}>
      <Icon className="h-5 w-5 mt-0.5 shrink-0" />
      <div>
        <div className="font-semibold text-sm mb-1">{title}</div>
        <div className="text-sm opacity-90">{body}</div>
      </div>
    </div>
  );
};

// (CheckCircle2 left in lucide imports for future success-screen variant)
export default Invite;
